import Icon from '@/components/ui/AppIcon';

interface ComplianceItem {
  jurisdiction: string;
  status: 'compliant' | 'partial' | 'restricted';
  requirements: string[];
  documentation: string;
}

interface ComplianceMatrixProps {
  items: ComplianceItem[];
}

export default function ComplianceMatrix({ items }: ComplianceMatrixProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'text-success';
      case 'partial':
        return 'text-warning';
      case 'restricted':
        return 'text-error';
      default:
        return 'text-text-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'CheckCircleIcon';
      case 'partial':
        return 'ExclamationTriangleIcon';
      case 'restricted':
        return 'XCircleIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  return (
    <div className="bg-surface border border-border rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-4 text-left font-headline font-semibold text-sm text-primary">
                Jurisdiction
              </th>
              <th className="px-6 py-4 text-left font-headline font-semibold text-sm text-primary">
                Compliance Status
              </th>
              <th className="px-6 py-4 text-left font-headline font-semibold text-sm text-primary">
                Requirements
              </th>
              <th className="px-6 py-4 text-left font-headline font-semibold text-sm text-primary">
                Documentation
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((item, index) => (
              <tr key={index} className="hover:bg-muted/50 transition-colors duration-smooth">
                <td className="px-6 py-4">
                  <span className="font-body text-sm text-text-primary font-medium">
                    {item.jurisdiction}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon
                      name={getStatusIcon(item.status) as any}
                      size={20}
                      variant="solid"
                      className={getStatusColor(item.status)}
                    />
                    <span className={`font-body text-sm font-medium capitalize ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <ul className="space-y-1">
                    {item.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="font-body text-xs text-text-secondary">
                        • {req}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-6 py-4">
                  <span className="font-accent text-xs text-accent hover:underline cursor-pointer">
                    {item.documentation}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}